import React, { useState } from 'react';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import EventAvailableOutlinedIcon from '@mui/icons-material/EventAvailableOutlined';
import CloudDoneOutlinedIcon from '@mui/icons-material/CloudDoneOutlined';
import AutorenewOutlinedIcon from '@mui/icons-material/AutorenewOutlined';
import ReplayOutlinedIcon from '@mui/icons-material/ReplayOutlined';
import SubscriptionDialog, { DialogButton, DialogNotice } from '../../../../components/subscription/SubscriptionDialog';
import styles from './CancelSubscriptionModal.module.css';

/**
 * Cancelamento em duas etapas: 1) o que acontece; 2) motivo obrigatório.
 * "Manter assinatura" é sempre a ação principal; confirmar fica em vermelho discreto.
 *
 * Props:
 *   isOpen            {bool}
 *   onClose           {function}
 *   onConfirm         {function(cancelReason, cancelReasonDetails)}
 *   isLoading         {bool}
 *   success           {null | true | false}
 *   subscriptionEndDate {string}  — data formatada do fim do período atual (ex: "15/06/2026")
 */

const CANCEL_REASONS = [
  { key: 'too_expensive',    label: 'Achei o valor alto' },
  { key: 'not_using',        label: 'Não estou utilizando o sistema' },
  { key: 'missing_feature',  label: 'Falta alguma funcionalidade importante' },
  { key: 'hard_to_use',      label: 'Tive dificuldades de uso' },
  { key: 'switching_system', label: 'Estou migrando para outro sistema' },
  { key: 'closing_clinic',   label: 'Encerramento das atividades da clínica' },
  { key: 'other',            label: 'Outro' },
];

const CancelSubscriptionModal = ({
  isOpen,
  onClose,
  onConfirm,
  isLoading = false,
  success = null,
  subscriptionEndDate = '',
}) => {
  const [step, setStep] = useState(1);
  const [selectedReason, setSelectedReason] = useState('');
  const [reasonDetails, setReasonDetails] = useState('');

  if (!isOpen) return null;

  const handleConfirm = () => {
    if (!selectedReason) return;
    onConfirm(selectedReason, reasonDetails.trim() || null);
  };

  const handleClose = () => {
    if (!isLoading) {
      setStep(1);
      setSelectedReason('');
      setReasonDetails('');
      onClose();
    }
  };

  const keepButton = (
    <DialogButton onClick={handleClose} disabled={isLoading}>
      Manter assinatura
    </DialogButton>
  );

  if (success === true) {
    return (
      <SubscriptionDialog
        open
        onClose={handleClose}
        centered
        icon={<span className={styles.successIcon}><CheckRoundedIcon fontSize="inherit" /></span>}
        title="Assinatura cancelada"
        subtitle={subscriptionEndDate
          ? `Seu acesso continua até ${subscriptionEndDate}. Seus dados ficam salvos.`
          : 'Seu acesso continua até o fim do período atual. Seus dados ficam salvos.'}
        actions={<DialogButton variant="outlined" onClick={handleClose}>Fechar</DialogButton>}
      />
    );
  }

  const failed = success === false;

  // Etapa 1: o que acontece
  if (step === 1 && !failed) {
    const facts = [
      {
        icon: <EventAvailableOutlinedIcon fontSize="inherit" />,
        title: subscriptionEndDate ? `Acesso até ${subscriptionEndDate}` : 'Acesso até o fim do período atual',
        text: 'Tudo continua funcionando normalmente até lá.',
      },
      {
        icon: <AutorenewOutlinedIcon fontSize="inherit" />,
        title: 'Sem novas cobranças',
        text: 'A assinatura não é renovada automaticamente.',
      },
      {
        icon: <CloudDoneOutlinedIcon fontSize="inherit" />,
        title: 'Seus dados ficam salvos',
        text: 'Nada é apagado ao cancelar.',
      },
      {
        icon: <ReplayOutlinedIcon fontSize="inherit" />,
        title: 'Pode voltar quando quiser',
        text: 'É só escolher um plano de novo.',
      },
    ];

    return (
      <SubscriptionDialog
        open
        onClose={handleClose}
        title="Cancelar assinatura"
        subtitle="Antes de confirmar, veja o que acontece."
        secondaryAction={(
          <DialogButton variant="text" onClick={() => setStep(2)}>
            Continuar cancelamento
          </DialogButton>
        )}
        actions={keepButton}
      >
        <span className={styles.stepPill}>Etapa 1 de 2</span>
        <ul className={styles.facts}>
          {facts.map((fact) => (
            <li key={fact.title} className={styles.fact}>
              <span className={styles.factIcon}>{fact.icon}</span>
              <span className={styles.factText}>
                <strong>{fact.title}</strong>
                <span>{fact.text}</span>
              </span>
            </li>
          ))}
        </ul>
        <p className={styles.disclaimer}>
          Ao voltar, alguns planos menores podem pedir ajuste nos limites de usuários, profissionais e agendas.
          Para encerrar a conta e eliminar os dados, use Backup e dados.
        </p>
      </SubscriptionDialog>
    );
  }

  // Etapa 2: motivo (e erro, se a tentativa falhou)
  return (
    <SubscriptionDialog
      open
      onClose={handleClose}
      locked={isLoading}
      title="Cancelar assinatura"
      subtitle="Qual o principal motivo? Sua resposta ajuda a melhorar a Gabriela."
      secondaryAction={(
        <>
          {!failed && (
            <DialogButton variant="text" onClick={() => setStep(1)} disabled={isLoading}>
              Voltar
            </DialogButton>
          )}
          <DialogButton
            variant="danger"
            onClick={handleConfirm}
            disabled={!selectedReason}
            loading={isLoading}
            loadingLabel={failed ? 'Tentando...' : 'Cancelando...'}
          >
            {failed ? 'Tentar novamente' : 'Confirmar cancelamento'}
          </DialogButton>
        </>
      )}
      actions={keepButton}
    >
      <span className={styles.stepPill}>Etapa 2 de 2</span>

      <div className={styles.reasons} role="radiogroup" aria-label="Motivo do cancelamento">
        {CANCEL_REASONS.map((reason) => (
          <label
            key={reason.key}
            className={`${styles.reason} ${selectedReason === reason.key ? styles.reasonSelected : ''}`}
          >
            <input
              type="radio"
              name="cancelReason"
              value={reason.key}
              checked={selectedReason === reason.key}
              onChange={() => setSelectedReason(reason.key)}
              className={styles.radioInput}
              disabled={isLoading}
            />
            <span>{reason.label}</span>
          </label>
        ))}
      </div>

      {selectedReason && (
        <textarea
          className={styles.textarea}
          placeholder={selectedReason === 'other' ? 'Conte um pouco mais (opcional)' : 'Quer contar mais? (opcional)'}
          value={reasonDetails}
          onChange={(e) => setReasonDetails(e.target.value)}
          rows={3}
          maxLength={500}
          disabled={isLoading}
        />
      )}

      {failed && (
        <DialogNotice tone="error" icon={<ErrorOutlineIcon fontSize="inherit" />}>
          Não foi possível cancelar sua assinatura. Tente novamente.
        </DialogNotice>
      )}
    </SubscriptionDialog>
  );
};

export default CancelSubscriptionModal;
